import type { NumberFormatOptions } from 'next-intl'
import { useCallback } from 'react'

import { DEFAULT_CURRENCY_CODE } from '../utils/currency'
import { useNumberFormatter } from './number'
import type { FormatterInputScalar } from './types'

type CurrencyOptions = NumberFormatOptions & {
  invalidStr?: string
  auto?: boolean
}

export const useCurrencyFormatter = () => {
  const formatNumber = useNumberFormatter()

  return useCallback(
    (value: FormatterInputScalar, options: CurrencyOptions) => {
      const { auto, ...rest } = options
      return formatNumber(value, { style: 'currency', maximumFractionDigits: auto ? 10 : undefined, ...rest })
    },
    [formatNumber]
  )
}

export function getCurrencySymbol(currency: string = DEFAULT_CURRENCY_CODE) {
  return Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  })
    .formatToParts(1)
    .find((part) => part.type === 'currency')?.value
}

/**
 * Returns whether the locale places the currency symbol before or after the amount.
 * Use for input adornments so symbol position matches user expectation (e.g. "€" after in many EU locales).
 */
export function getCurrencySymbolPosition(
  currency: string = DEFAULT_CURRENCY_CODE,
  locale: string = 'en-US'
): 'start' | 'end' {
  const parts = Intl.NumberFormat(locale, { style: 'currency', currency }).formatToParts(1)
  const currencyIndex = parts.findIndex((part) => part.type === 'currency')
  return currencyIndex === 0 ? 'start' : 'end'
}
